import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Grid, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SuperAdminService, { ISuperAdminErrorSummary, ISuperAdminFeatureUsage, ISuperAdminOrgUsage, ISuperAdminUsageSummary } from '../../../../shared/services/super-admin/super-admin.service';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar';
import { LoadingPage } from '../../loading-page';
import { useDebounce } from '../../../../hooks/use-debounce-hook';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { UsageDateFilter, UsageDateRange, defaultUsageRange } from './usage-date-filter';
import { UsageEventsByType, reportBreakdownItems, analyticsTabBreakdownItems, ssoProviderBreakdownItems } from './usage-events-by-type';
import { UsageStatCard, UsageBreakdownCard, UsageBreakdownList, UsageRateRow, UsageImportsList, USAGE_SETTINGS, USAGE_ENTITIES } from './usage-ui';
import { downloadCsv, toCsv } from '../../../../shared/utils/download-file-utils';

const QUIET_DAYS = 30;

const DOMAIN_LABELS: { [key: string]: string } = {
  supplier_spend_category: 'Supplier category',
  bill_line: 'Bill lines',
};

const EVENT_LABELS: { [key: string]: string } = {
  'user.logged_in': 'Logins',
  'import.uploaded': 'Imports uploaded',
  'import.committed': 'Imports committed',
  'report.generated': 'Reports generated',
  'report.exported': 'Reports exported',
  'report.viewed': 'Reports viewed',
  'report.analytics_generated': 'Analytics reports created',
  'dashboard.created': 'Dashboards created',
  'dashboard.viewed': 'Dashboards viewed',
  'analytics_dashboard.viewed': 'Analytics dashboard viewed',
  'hotspot.viewed': 'Hotspot analysis',
  'survey.created': 'Surveys created',
  'survey.sent': 'Surveys sent',
  'survey.response_received': 'Survey responses',
  'emission.reclassified_to_activity': 'Reclassified to activity',
  'mfa.enabled': '2FA enabled',
  'sso.enabled': 'SSO enabled',
  'sso.used': 'SSO used',
};

const formatDate = (value: string | null): string => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'Never';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Plain-text plan label, shared by the table chip and the CSV export. Subscriptions have no
// stored end date (subscriptionActive is a manual toggle), so only trials carry a countdown.
const planText = (org: ISuperAdminOrgUsage): string => {
  if (org.plan === 'SUBSCRIBED') return 'Subscribed';
  if (org.plan === 'TRIAL') return `Trial (${org.trialDaysRemaining}d left)`;
  return 'Expired';
};

const planChip = (org: ISuperAdminOrgUsage) => {
  const color = org.plan === 'SUBSCRIBED' ? 'success' : org.plan === 'TRIAL' ? 'info' : 'error';
  return <Chip label={planText(org)} size="small" color={color} variant="outlined" />;
};

export const SuperAdminUsagePage = () => {
  const navigate = useNavigate();
  const superAdminService = new SuperAdminService();
  const userNotifications = useContext(UserNotificationsContext);
  const [tab, setTab] = useState(0);
  const [range, setRange] = useState<UsageDateRange>(defaultUsageRange());
  // Organisations tab
  const [summary, setSummary] = useState<ISuperAdminUsageSummary | null>(null);
  const [orgs, setOrgs] = useState<ISuperAdminOrgUsage[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [change, setChange] = useState(0);
  const [hasPageMounted, setHasPageMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quietOnly, setQuietOnly] = useState(false);
  const [orderBy, setOrderBy] = useState('lastActivity');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [exportingOrgs, setExportingOrgs] = useState(false);
  // Functionality tab
  const [features, setFeatures] = useState<ISuperAdminFeatureUsage | null>(null);
  // Errors tab
  const [errors, setErrors] = useState<ISuperAdminErrorSummary | null>(null);
  const [errorPage, setErrorPage] = useState(0);
  const [errorPerPage, setErrorPerPage] = useState(25);
  const [errorSelected, setErrorSelected] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [exportingErrors, setExportingErrors] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const requestId = useRef(0);

  useEffect(() => {
    superAdminService.getUsageSummary(range.from, range.to, QUIET_DAYS).then(setSummary).catch(err => console.log(err));
    superAdminService.getFeatureUsage(range.from, range.to).then(setFeatures).catch(err => console.log(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const loadErrors = () => superAdminService.getErrorSummary(range.from, range.to, errorPage, errorPerPage).then(setErrors).catch(err => console.log(err));
  useEffect(() => {
    loadErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, errorPage, errorPerPage]);

  useEffect(() => {
    requestId.current++;
    const currentRequestId = requestId.current;
    superAdminService.getOrganisationUsage(debouncedSearchQuery, inputPage, perPage, range.from, range.to, QUIET_DAYS, { orderBy, order, quietOnly })
      .then(response => {
        if (currentRequestId === requestId.current) {
          setTotalRecords(response.count);
          setOrgs(response.rows);
          setIsLoading(false);
        }
      })
      .catch(err => { if (currentRequestId === requestId.current) setIsLoading(false); console.log(err); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputPage, perPage, change, range, quietOnly, orderBy, order]);

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) setChange(c => c + 1);
      else setInputPage(0);
    }
    setHasPageMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  const handleExportOrgs = async () => {
    setExportingOrgs(true);
    try {
      const res = await superAdminService.exportOrganisationUsage(range.from, range.to, QUIET_DAYS, { search: debouncedSearchQuery, orderBy, order, quietOnly });
      const headers = ['Organisation', 'Last active', 'Last login', 'Logins', 'Uploaded', 'Committed', 'Reports', 'Surveys', 'Errors', 'Plan', 'Trial ends', 'Status'];
      const csvRows = res.rows.map(o => [o.name, formatDate(o.lastActivityAt), formatDate(o.lastLoginAt), o.logins, o.uploads, o.imports, o.reports, o.surveys, o.errors, planText(o), o.plan === 'TRIAL' ? formatDate(o.trialEndsAt) : '', o.isQuiet ? 'Quiet' : 'Active']);
      downloadCsv({ csv: toCsv(headers, csvRows), fileName: 'usage-organisations.csv' });
    } catch (err) {
      userNotifications.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    } finally {
      setExportingOrgs(false);
    }
  };

  const handleExportErrors = async () => {
    setExportingErrors(true);
    try {
      const res = await superAdminService.exportErrors(range.from, range.to);
      const headers = ['When', 'Status', 'Method', 'Path', 'Route', 'Message', 'Organisation'];
      const csvRows = res.rows.map(e => [formatDateTime(e.occurredAt), e.statusCode, e.method || '', e.path || '', e.routePattern || '', e.message || '', e.organisationName || '']);
      downloadCsv({ csv: toCsv(headers, csvRows), fileName: 'usage-errors.csv' });
    } catch (err) {
      userNotifications.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    } finally {
      setExportingErrors(false);
    }
  };

  const handleDeleteOld = async () => {
    const confirmed = await userNotifications.openDialog({
      active: true,
      title: 'Delete old errors?',
      message: 'This permanently deletes every error log older than 60 days. It cannot be undone.',
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    });
    if (!confirmed) return;
    setDeleting(true);
    try {
      const res = await superAdminService.deleteOldErrors(60);
      userNotifications.openSnackbar({ open: true, message: `Deleted ${res.deleted} error${res.deleted === 1 ? '' : 's'} older than 60 days` });
      loadErrors();
    } catch (err) {
      userNotifications.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    } finally {
      setDeleting(false);
    }
  };

  const kpis = summary ? [
    { label: 'Organisations', value: summary.totals.totalOrganisations, icon: <ApartmentOutlinedIcon />, accent: 'primary' as const, previous: summary.previous.totalOrganisations },
    { label: `Active (${summary.windowDays}d)`, value: summary.totals.activeOrganisations, icon: <BoltOutlinedIcon />, accent: 'success' as const, previous: summary.previous.activeOrganisations },
    { label: 'Signed in now', value: summary.totals.signedInNow, icon: <PeopleAltOutlinedIcon />, accent: 'info' as const, previous: undefined as number | undefined },
    { label: `Quiet (${summary.quietDays}d)`, value: summary.totals.quietOrganisations, icon: <BedtimeOutlinedIcon />, accent: 'warning' as const, previous: undefined as number | undefined },
    { label: `Events (${summary.windowDays}d)`, value: summary.totals.events, icon: <InsightsOutlinedIcon />, accent: 'info' as const, previous: summary.previous.events },
    { label: `Suggestions (${summary.windowDays}d)`, value: summary.totals.suggestionOutcomes, icon: <AutoAwesomeOutlinedIcon />, accent: 'primary' as const, previous: summary.previous.suggestionOutcomes },
  ] : [];

  const headCells: IHeadCell[] = [
    { id: 'name', numeric: false, label: 'Organisation', sortable: true },
    { id: 'lastActivity', numeric: false, label: 'Last active', sortable: true },
    { id: 'logins', numeric: true, label: 'Logins', sortable: true },
    { id: 'uploads', numeric: true, label: 'Uploaded', sortable: true },
    { id: 'imports', numeric: true, label: 'Committed', sortable: true },
    { id: 'reports', numeric: true, label: 'Reports', sortable: true },
    { id: 'surveys', numeric: true, label: 'Surveys', sortable: true },
    { id: 'errors', numeric: true, label: 'Errors', sortable: true },
    { id: 'plan', numeric: false, label: 'Plan', sortable: false },
    { id: 'status', numeric: false, label: 'Status', sortable: false },
  ];

  const rows = orgs.map(org => ({
    id: org.organisationId,
    name: org.name,
    lastActivity: formatDate(org.lastActivityAt),
    logins: org.logins,
    uploads: org.uploads,
    imports: org.imports,
    reports: org.reports,
    surveys: org.surveys,
    errors: org.errors,
    plan: planChip(org),
    status: org.isQuiet ? <Chip label="Quiet" size="small" color="warning" /> : <Chip label="Active" size="small" color="success" />,
  }));

  const topReportItems = (features?.topReports || []).map(r => ({ label: r.name || r.analyticReportId, count: r.count }));
  const fc = features?.featureCounts;
  const fcp = features?.featureCountsPrev;
  const featureItems = fc && fcp ? [
    { label: 'Custom dashboards created', count: fc.dashboardsCreated, previous: fcp.dashboardsCreated },
    { label: 'Custom dashboards viewed', count: fc.dashboardsViewed, previous: fcp.dashboardsViewed },
    { label: 'Hotspot analysis run', count: fc.hotspotViewed, previous: fcp.hotspotViewed },
    { label: 'Financial reclassified to activity', count: fc.reclassifications, previous: fcp.reclassifications },
    { label: 'SSO used', count: fc.ssoUsed, previous: fcp.ssoUsed },
    { label: 'SSO enabled', count: fc.ssoEnabled, previous: fcp.ssoEnabled },
    { label: '2FA enabled', count: fc.mfaEnabled, previous: fcp.mfaEnabled },
  ] : [];

  const errorStatusItems = (errors?.byStatus || []).map(s => ({ label: String(s.statusCode), count: s.count }));
  const errorEndpointItems = (errors?.byEndpoint || []).map(e => ({ label: `${e.method || ''} ${e.routePattern || ''}`.trim(), count: e.count }));
  const errorMessageItems = (errors?.byMessage || []).map(m => ({ label: m.message, count: m.count }));

  const errorHeadCells: IHeadCell[] = [
    { id: 'when', numeric: false, label: 'When', sortable: false },
    { id: 'statusCode', numeric: true, label: 'Status', sortable: false },
    { id: 'method', numeric: false, label: 'Method', sortable: false },
    { id: 'path', numeric: false, label: 'Path', sortable: false },
    { id: 'message', numeric: false, label: 'Message', sortable: false },
    { id: 'org', numeric: false, label: 'Organisation', sortable: false },
  ];
  const errorRows = (errors?.recent || []).map((e, idx) => ({
    id: `${e.occurredAt}-${idx}`,
    when: formatDateTime(e.occurredAt),
    statusCode: e.statusCode,
    method: e.method || '',
    path: e.path || '',
    message: e.message || '',
    org: e.organisationName || '',
  }));

  return (
    <PageOuterContainer>
      <PageHeading title='Usage' includeBackButton={true} backButtonLink='/super-admin' backButtonText='Back to super admin' hasTabs={false} />
      <Box sx={{ mb: 2 }}>
        <UsageDateFilter onChange={setRange} />
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, next) => setTab(next)} aria-label="usage tabs">
          <Tab label="Organisations" {...a11yProps(0)} />
          <Tab label="Functionality" {...a11yProps(1)} />
          <Tab label="Errors" {...a11yProps(2)} />
        </Tabs>
      </Box>

      <CustomTabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {kpis.map(kpi => (
            <Box key={kpi.label} sx={{ flex: '1 1 180px', minWidth: 150 }}>
              <UsageStatCard label={kpi.label} value={kpi.value} icon={kpi.icon} accent={kpi.accent} previous={kpi.previous} />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ width: { xs: '100%', sm: 320 } }}>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={quietOnly ? 'quiet' : 'all'}
            onChange={(_, v) => { if (v !== null) { setQuietOnly(v === 'quiet'); setInputPage(0); } }}
          >
            <ToggleButton value="all"><Typography variant="body2" sx={{ textTransform: 'none' }}>All</Typography></ToggleButton>
            <ToggleButton value="quiet"><Typography variant="body2" sx={{ textTransform: 'none' }}>Quiet only</Typography></ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" disabled={exportingOrgs} onClick={handleExportOrgs} startIcon={<DownloadOutlinedIcon fontSize="small" />}>
            <Typography sx={{ pl: 0.5 }}>{exportingOrgs ? 'Exporting...' : 'Export CSV'}</Typography>
          </Button>
        </Box>
        <LoadingPage pageReady={!isLoading}>
          <TableDisplay
            headCells={headCells}
            rows={rows}
            onRowClick={(id) => navigate(`/super-admin/usage/organisations/${id}`)}
            onOrderChange={(ob, o) => { setOrderBy(ob); setOrder(o); setInputPage(0); }}
            selected={selected}
            setSelected={setSelected}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage}
          />
        </LoadingPage>
      </CustomTabPanel>

      <CustomTabPanel value={tab} index={1}>
        {!features ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading...</Typography>
        ) : (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>Arrows show the change against the previous {features.windowDays} days.</Typography>
            <Box sx={{ columnGap: 3, columnCount: { xs: 1, md: 2 } }}>
              <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                <UsageBreakdownCard title={`Events by type (${features.windowDays}d)`}>
                  <UsageEventsByType
                    events={features.eventsByType}
                    breakdowns={{
                      'report.viewed': reportBreakdownItems(features.reportsByType),
                      'analytics_dashboard.viewed': analyticsTabBreakdownItems(features.analyticsDashboardByTab),
                      'sso.used': ssoProviderBreakdownItems(features.ssoByProvider, 'sso.used'),
                      'sso.enabled': ssoProviderBreakdownItems(features.ssoByProvider, 'sso.enabled'),
                    }}
                    labels={EVENT_LABELS}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>Click a row with an arrow (Reports viewed, Analytics dashboard viewed, SSO) to see the breakdown.</Typography>
                </UsageBreakdownCard>
              </Box>
              <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                <UsageBreakdownCard title="Imports by path">
                  <UsageImportsList items={features.importsByPath || []} />
                </UsageBreakdownCard>
              </Box>
              <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                <UsageBreakdownCard title="Records added">
                  <UsageBreakdownList items={USAGE_ENTITIES.map(e => ({ label: e.label, count: (features.entitiesAdded as any)[e.key] || 0, previous: (features.entitiesAddedPrev as any)[e.key] || 0 }))} />
                </UsageBreakdownCard>
              </Box>
              <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                <UsageBreakdownCard title="Settings adoption">
                  <Stack spacing={1}>
                    {USAGE_SETTINGS.map(s => {
                      const on = (features.settingsAdoption as any)[s.key] || 0;
                      const total = features.settingsAdoption.totalOrganisations || 0;
                      return <UsageRateRow key={s.key} label={s.label} detail={`${on} of ${total}`} percent={total ? (on / total) * 100 : 0} />;
                    })}
                  </Stack>
                </UsageBreakdownCard>
              </Box>
              <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                <UsageBreakdownCard title="Most-used analytics reports">
                  <UsageBreakdownList items={topReportItems} />
                </UsageBreakdownCard>
              </Box>
              <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                <UsageBreakdownCard title="Feature counts">
                  <UsageBreakdownList items={featureItems} />
                </UsageBreakdownCard>
              </Box>
              <Box sx={{ breakInside: 'avoid', mb: 2 }}>
                <UsageBreakdownCard title="Suggestion acceptance">
                  {(summary?.suggestionAcceptance || []).length === 0
                    ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nothing recorded in this window.</Typography>
                    : <Stack spacing={1}>
                      {(summary?.suggestionAcceptance || []).map(item => (
                        <UsageRateRow key={item.domain} label={DOMAIN_LABELS[item.domain] || item.domain} detail={`${item.accepted}/${item.total} (${item.acceptanceRate}%)`} percent={item.acceptanceRate} />
                      ))}
                    </Stack>}
                </UsageBreakdownCard>
              </Box>
            </Box>
          </>
        )}
      </CustomTabPanel>

      <CustomTabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.primary' }}>{errors ? errors.total : 0} errors in range</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" disabled={exportingErrors} onClick={handleExportErrors} startIcon={<DownloadOutlinedIcon fontSize="small" />}>
              <Typography sx={{ pl: 0.5 }}>{exportingErrors ? 'Exporting...' : 'Export CSV'}</Typography>
            </Button>
            <Button variant="outlined" disabled={deleting} onClick={handleDeleteOld}>
              <Typography sx={{ px: 0.5 }}>Delete errors older than 60 days</Typography>
            </Button>
          </Box>
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <UsageBreakdownCard title="By status code">
              <UsageBreakdownList items={errorStatusItems} emptyText="No errors in this window." />
            </UsageBreakdownCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <UsageBreakdownCard title="Top failing endpoints">
              <UsageBreakdownList items={errorEndpointItems} emptyText="No errors in this window." />
            </UsageBreakdownCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <UsageBreakdownCard title="Top messages">
              <UsageBreakdownList items={errorMessageItems} emptyText="No errors in this window." />
            </UsageBreakdownCard>
          </Grid>
        </Grid>
        <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1 }}>Recent errors</Typography>
        <TableDisplay
          headCells={errorHeadCells}
          rows={errorRows}
          selected={errorSelected}
          setSelected={setErrorSelected}
          inputPage={errorPage}
          setInputPage={setErrorPage}
          totalRecords={errors ? errors.recentCount : 0}
          perPage={errorPerPage}
          setPerPage={setErrorPerPage}
        />
      </CustomTabPanel>
    </PageOuterContainer>
  );
};
